import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

interface PageNavigatorProps {
  file: File | null;
  pageCount: number;
  onPageSelect: (pageIndex: number) => void;
  /** Optional live thumbnails from the main viewer (much faster + accurate) */
  liveThumbnails?: Record<number, string>;
  /** Shared pdfjs document from usePdfDocument — avoids a second parse of the same file. */
  sharedPdfDoc?: pdfjsLib.PDFDocumentProxy | null;
  variant?: 'rail' | 'filmstrip';
  activePageIndex?: number;
  /**
   * Filmstrip only. Called when the user drag-and-drops a page thumbnail to a new position.
   * order[newPosition] = oldIndex — same convention as the server reorder action.
   * When undefined, drag-to-reorder is disabled.
   */
  onReorderPages?: (order: number[]) => void;
}

// BUG-13: ITEM_HEIGHT was 64 but real rendered height (border+padding+image+gap) is ~74px.
// We now measure a sentinel element via useLayoutEffect and fall back to this estimate.
const ITEM_HEIGHT_ESTIMATE = 74;
const OVERSCAN = 8; // render this many extra rows above + below viewport for buttery scroll
const VIEWPORT_H = 240; // must match the max-h-[240px] container

/**
 * Page thumbnails / navigator for quick navigation.
 * VIRTUALIZED: only mounts ~10-16 DOM nodes + images even for 100-200 page PDFs.
 * Zero dependencies. Uses scroll position + fixed item height to compute visible range.
 * Lazy-generates only visible (+overscan) thumbnails via a kept-alive pdfjs doc ref.
 * Strongly prefers liveThumbnails (from main viewer IntersectionObserver pages) when present.
 */
export const PageNavigator: React.FC<PageNavigatorProps> = ({
  file,
  pageCount,
  onPageSelect,
  liveThumbnails = {},
  sharedPdfDoc,
  variant = 'rail',
  activePageIndex = 0,
  onReorderPages,
}) => {
  const [thumbMap, setThumbMap] = useState<Record<number, string>>({});
  const [scrollTop, setScrollTop] = useState(0);
  // BUG-13: measure actual item height so virtualization window is accurate
  const [itemHeight, setItemHeight] = useState(ITEM_HEIGHT_ESTIMATE);
  const sentinelItemRef = useRef<HTMLButtonElement | null>(null);

  const pdfDocRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null);
  const usesSharedDocRef = useRef(sharedPdfDoc !== undefined);
  usesSharedDocRef.current = sharedPdfDoc !== undefined;
  const loadingThumbsRef = useRef<Set<number>>(new Set());
  // FIX-5: track active RenderTasks by page index so we can cancel() them when the
  // component unmounts, the doc switches, or a new render starts for the same page.
  // Without this, page.render() runs against a destroyed PDFDocumentProxy and throws.
  const renderTasksRef = useRef<Map<number, { cancel: () => void }>>(new Map());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(true);
  // Ref mirror of thumbMap so ensureThumb closure always sees latest without causing effect loops
  const thumbMapRef = useRef<Record<number, string>>({});

  // Keep isMounted for async guards
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // FIX-5: cancel all in-flight render tasks before destroying the proxy so
      // page.render() never executes against a destroyed PDFDocumentProxy.
      renderTasksRef.current.forEach((task) => { try { task.cancel(); } catch { /* ignore */ } });
      renderTasksRef.current.clear();
      if (pdfDocRef.current && !usesSharedDocRef.current) {
        pdfDocRef.current.destroy().catch(() => {});
        pdfDocRef.current = null;
      }
    };
  }, []);

  // BUG-13: measure first rendered item to get the true height (border+padding+gap).
  // cli-perf-5: observe the sentinel element with a ResizeObserver instead of reading
  // offsetHeight on every render. This fires only when the element's box actually changes
  // (mount, thumbnail/image load, layout reflow) — removing the forced synchronous layout
  // from the navigator's (frequently re-rendering) scroll / liveThumbnails / thumbMap path.
  useLayoutEffect(() => {
    if (variant === 'filmstrip') return; // filmstrip is horizontal, no height virtualization
    const el = sentinelItemRef.current;
    if (!el) return;

    const measure = () => {
      // offsetHeight gives the element's own box; add gap from space-y-1 (4px)
      const h = el.offsetHeight + 4; // 4px = Tailwind space-y-1
      if (h > 0) {
        // functional update avoids needing itemHeight in deps and self-guards no-op writes
        setItemHeight((prev) => (h !== prev ? h : prev));
      }
    };

    // Measure synchronously once for the current layout…
    measure();

    // …then only re-measure when the sentinel's box genuinely changes.
    if (typeof ResizeObserver === 'undefined') return; // SSR / very old browsers: single measure is fine
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
    // Re-bind when the sentinel element can change (variant switch / first vs. no pages,
    // i.e. pageCount crossing 0). The observer itself handles all size changes in between.
  }, [variant, pageCount]);

  // Merge incoming liveThumbnails (they win for display and prevent re-generation)
  useEffect(() => {
    if (!liveThumbnails || Object.keys(liveThumbnails).length === 0) return;
    setThumbMap((prev) => {
      let mutated = false;
      const next = { ...prev };
      Object.keys(liveThumbnails).forEach((k) => {
        const idx = Number(k);
        const v = liveThumbnails[idx];
        if (v && next[idx] !== v) {
          next[idx] = v;
          mutated = true;
        }
      });
      return mutated ? next : prev;
    });
  }, [liveThumbnails]);

  // Keep thumbMapRef in sync (after every thumbMap change)
  useEffect(() => {
    thumbMapRef.current = thumbMap;
  }, [thumbMap]);

  // Lightweight scroll handler (updates range computation cheaply)
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  // Ref mirror of liveThumbnails so ensureThumb closure always sees latest without
  // causing effect loops (same rationale as thumbMapRef). liveThumbnails gets a brand-new
  // object identity on every page the main viewer reports; keeping it out of ensureThumb's
  // deps stops the visible-window / loader / filmstrip effects from re-running on each update.
  const liveThumbnailsRef = useRef<Record<number, string>>(liveThumbnails);
  liveThumbnailsRef.current = liveThumbnails;

  // Ensure thumbs for the current visible + overscan window (and live wins automatically).
  // Declared early so the pdf loader effect below can reference it safely.
  const ensureThumb = useCallback(async (pageIdx: number) => {
    if (pageIdx < 0 || pageIdx >= pageCount) return;
    // Live always wins – if present use it (and store). Read through the ref so this
    // callback keeps a stable identity across liveThumbnails updates.
    const live = liveThumbnailsRef.current[pageIdx];
    if (live) {
      setThumbMap((m) => (m[pageIdx] === live ? m : { ...m, [pageIdx]: live }));
      return;
    }
    // Use ref for fresh check to avoid stale closure + effect churn
    if (thumbMapRef.current[pageIdx] || loadingThumbsRef.current.has(pageIdx)) return;

    const pdf = pdfDocRef.current;
    if (!pdf) return;

    loadingThumbsRef.current.add(pageIdx);
    try {
      const page = await pdf.getPage(pageIdx + 1);
      // Guard: doc may have been destroyed while we awaited getPage
      if (!isMountedRef.current || pdfDocRef.current !== pdf) return;

      const viewport = page.getViewport({ scale: 0.12 });

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { alpha: false })!;
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      // FIX-5: store the RenderTask so it can be cancelled on unmount or doc-switch,
      // preventing render calls against a destroyed PDFDocumentProxy.
      // Cancel any previous task for this page (shouldn't normally occur, but be safe).
      const prevTask = renderTasksRef.current.get(pageIdx);
      if (prevTask) { try { prevTask.cancel(); } catch { /* ignore */ } }

      const renderTask = page.render({ canvasContext: ctx, viewport });
      renderTasksRef.current.set(pageIdx, renderTask);

      await renderTask.promise;

      renderTasksRef.current.delete(pageIdx);

      const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
      // cleanup canvas memory immediately
      canvas.width = 0;
      canvas.height = 0;

      if (isMountedRef.current) {
        setThumbMap((m) => ({ ...m, [pageIdx]: dataUrl }));
      }
    } catch (err: any) {
      // FIX-5: cancellation is expected on unmount/doc-switch — don't log as an error.
      if (err?.name === 'RenderingCancelledException') return;
      // Non-fatal per-page; user can still navigate, thumb just missing for this page
      if (process.env.NODE_ENV === 'development') console.debug('thumb gen skipped', pageIdx, err);
    } finally {
      loadingThumbsRef.current.delete(pageIdx);
    }
  }, [pageCount]);

  // Compute visible window (pure, re-runs on scroll or count change)
  // BUG-13: use measured itemHeight instead of the stale constant
  const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - OVERSCAN);
  const endIndex = Math.min(pageCount, Math.ceil((scrollTop + VIEWPORT_H) / itemHeight) + OVERSCAN);

  // Use shared pdfjs doc when provided; otherwise load locally (legacy / LeftRail path).
  useEffect(() => {
    if (sharedPdfDoc !== undefined) {
      pdfDocRef.current = sharedPdfDoc;
      if (!sharedPdfDoc || pageCount === 0) {
        setThumbMap({});
        setScrollTop(0);
        loadingThumbsRef.current.clear();
        return;
      }
      const primeCount = Math.min(10, pageCount);
      for (let i = 0; i < primeCount; i++) {
        void ensureThumb(i);
      }
      return;
    }

    if (!file || pageCount === 0) {
      // FIX-5: cancel in-flight renders before destroying the proxy.
      renderTasksRef.current.forEach((task) => { try { task.cancel(); } catch { /* ignore */ } });
      renderTasksRef.current.clear();
      if (pdfDocRef.current) {
        pdfDocRef.current.destroy().catch(() => {});
        pdfDocRef.current = null;
      }
      setThumbMap({});
      setScrollTop(0);
      loadingThumbsRef.current.clear();
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
        const pdf = await loadingTask.promise;
        if (cancelled || !isMountedRef.current) {
          pdf.destroy();
          return;
        }
        if (pdfDocRef.current) {
          // FIX-5: cancel renders using the OLD doc before destroying it.
          renderTasksRef.current.forEach((task) => { try { task.cancel(); } catch { /* ignore */ } });
          renderTasksRef.current.clear();
          pdfDocRef.current.destroy().catch(() => {});
        }
        pdfDocRef.current = pdf;

        const primeCount = Math.min(10, pageCount);
        for (let i = 0; i < primeCount; i++) {
          void ensureThumb(i);
        }
      } catch (err) {
        console.error('Failed to open PDF for thumbnail virtualization', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [file, pageCount, ensureThumb, sharedPdfDoc]);

  // Trigger lazy generation when visible window moves (scroll / initial)
  useEffect(() => {
    if (pageCount === 0) return;
    for (let i = startIndex; i < endIndex; i++) {
      void ensureThumb(i);
    }
  }, [startIndex, endIndex, pageCount, ensureThumb]);

  const thumbButtonRefs = useRef<Record<number, HTMLButtonElement | null>>({});

  // ── Drag-to-reorder state (filmstrip only) ──────────────────────────────────
  // We store drag state in refs (not useState) to avoid triggering re-renders on every
  // dragover event — only dropTargetIndex needs to trigger a visual re-render.
  const dragSourceIndexRef = useRef<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent<HTMLButtonElement>, idx: number) => {
    dragSourceIndexRef.current = idx;
    // Required for Firefox; the data value is unused (we read from the ref).
    e.dataTransfer.setData('text/plain', String(idx));
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const dropTargetIndexRef = useRef<number | null>(null);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLButtonElement>, idx: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    // Use ref to avoid stale closure; only call setState when value actually changes
    if (dropTargetIndexRef.current !== idx) {
      dropTargetIndexRef.current = idx;
      setDropTargetIndex(idx);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLButtonElement>) => {
    // Only clear if leaving into something outside the filmstrip scroll container
    const related = e.relatedTarget as Node | null;
    if (!scrollContainerRef.current?.contains(related)) {
      dropTargetIndexRef.current = null;
      setDropTargetIndex(null);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLButtonElement>, dropIdx: number) => {
    e.preventDefault();
    dropTargetIndexRef.current = null;
    setDropTargetIndex(null);
    const sourceIdx = dragSourceIndexRef.current;
    dragSourceIndexRef.current = null;
    if (sourceIdx === null || sourceIdx === dropIdx || !onReorderPages) return;

    // Build new order array: identity [0, 1, 2, …, pageCount-1] then move sourceIdx to dropIdx.
    const order = Array.from({ length: pageCount }, (_, i) => i);
    // Remove the dragged page from its old position
    order.splice(sourceIdx, 1);
    // Insert it at the drop position (adjusted for the removal)
    const insertAt = sourceIdx < dropIdx ? dropIdx - 1 : dropIdx;
    order.splice(insertAt, 0, sourceIdx);

    onReorderPages(order);
  }, [pageCount, onReorderPages]);

  const handleDragEnd = useCallback(() => {
    dragSourceIndexRef.current = null;
    dropTargetIndexRef.current = null;
    setDropTargetIndex(null);
  }, []);

  const handleClick = (pageIndex: number) => {
    onPageSelect(pageIndex);

    const scrollRoot = document.querySelector('.editor-stage-viewer');
    const pageElement = document.getElementById(`pdf-page-${pageIndex}`);
    if (pageElement && scrollRoot) {
      const rootRect = scrollRoot.getBoundingClientRect();
      const pageRect = pageElement.getBoundingClientRect();
      scrollRoot.scrollTop += pageRect.top - rootRect.top - 12;
    } else if (pageElement) {
      pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const filmstripIndices = useMemo(() => {
    if (variant !== 'filmstrip' || pageCount === 0) return [];
    if (pageCount <= 60) return Array.from({ length: pageCount }, (_, i) => i);
    const base = Array.from({ length: Math.min(pageCount, 24) }, (_, i) => i);
    for (let i = Math.max(0, activePageIndex - 4); i <= Math.min(pageCount - 1, activePageIndex + 4); i++) {
      if (!base.includes(i)) base.push(i);
    }
    return base.sort((a, b) => a - b);
  }, [variant, pageCount, activePageIndex]);

  useEffect(() => {
    if (variant !== 'filmstrip') return;
    filmstripIndices.forEach((i) => void ensureThumb(i));
  }, [variant, filmstripIndices, ensureThumb]);

  useEffect(() => {
    if (variant !== 'filmstrip') return;
    const btn = thumbButtonRefs.current[activePageIndex];
    btn?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [activePageIndex, variant]);

  // Keep the active rail thumbnail visible when the viewer scroll changes the page.
  useEffect(() => {
    if (variant !== 'rail' || pageCount <= 1) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const targetTop = activePageIndex * itemHeight;
    const viewTop = container.scrollTop;
    const viewBottom = viewTop + VIEWPORT_H;
    const itemBottom = targetTop + itemHeight;
    if (targetTop < viewTop || itemBottom > viewBottom) {
      container.scrollTop = Math.max(0, targetTop - itemHeight * 2);
    }
  }, [activePageIndex, variant, itemHeight, pageCount]);

  if (pageCount === 0) return null;

  if (variant === 'filmstrip') {
    const isDragEnabled = !!onReorderPages;
    return (
      <div
        ref={scrollContainerRef}
        className="editor-pages-filmstrip-scroll"
        aria-label="Page thumbnails"
      >
        {filmstripIndices.map((idx) => {
          const dataUrl = liveThumbnails[idx] || thumbMap[idx];
          const isLoading = !dataUrl && loadingThumbsRef.current.has(idx);
          const isActive = idx === activePageIndex;
          const isDragTarget = dropTargetIndex === idx;

          return (
            <button
              key={idx}
              ref={(el) => {
                thumbButtonRefs.current[idx] = el;
              }}
              type="button"
              onClick={() => handleClick(idx)}
              className={`editor-pages-thumb ${isActive ? 'active' : ''}`}
              title={`Page ${idx + 1}${isDragEnabled ? ' (drag to reorder)' : ''}`}
              aria-label={`Page ${idx + 1}`}
              aria-current={isActive ? 'page' : undefined}
              draggable={isDragEnabled}
              onDragStart={isDragEnabled ? (e) => handleDragStart(e, idx) : undefined}
              onDragOver={isDragEnabled ? (e) => handleDragOver(e, idx) : undefined}
              onDragLeave={isDragEnabled ? handleDragLeave : undefined}
              onDrop={isDragEnabled ? (e) => handleDrop(e, idx) : undefined}
              onDragEnd={isDragEnabled ? handleDragEnd : undefined}
              style={isDragTarget ? {
                position: 'relative',
                outline: '2px solid var(--accent, #6366f1)',
                outlineOffset: 2,
              } : { position: 'relative' }}
            >
              {/* Drop indicator: left edge stripe showing insertion point */}
              {isDragTarget && (
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: '10%',
                    bottom: '10%',
                    width: 3,
                    borderRadius: 2,
                    background: 'var(--accent, #6366f1)',
                    pointerEvents: 'none',
                  }}
                />
              )}
              <div className="editor-pages-thumb-preview">
                {dataUrl ? (
                  <img src={dataUrl} alt="" loading="lazy" />
                ) : (
                  <div
                    className={isLoading ? 'animate-pulse bg-slate-200 dark:bg-slate-700 w-full h-full' : 'w-full h-full bg-slate-100 dark:bg-slate-800'}
                    aria-hidden
                  />
                )}
              </div>
              <span className="editor-pages-thumb-label">{idx + 1}</span>
            </button>
          );
        })}
      </div>
    );
  }

  if (pageCount <= 1) return null;

  // BUG-13: use measured itemHeight for accurate spacer sizing
  const topSpacerH = startIndex * itemHeight;
  const bottomSpacerH = Math.max(0, (pageCount - endIndex) * itemHeight);

  // For 80+ page docs we mount only a handful of <img> elements at any moment
  const visibleIndices = Array.from({ length: Math.max(0, endIndex - startIndex) }, (_, k) => startIndex + k);

  return (
    <div className="rail-section">
      <div className="rail-label">Pages</div>
      <div
        ref={scrollContainerRef}
        className="max-h-[240px] overflow-y-auto space-y-1 pr-1 custom-scrollbar"
        onScroll={handleScroll}
        aria-label="Page thumbnails navigator (virtualized)"
      >
        {/* Top spacer keeps exact scroll height + position for virtualization */}
        <div style={{ height: `${topSpacerH}px` }} aria-hidden="true" />

        {visibleIndices.map((idx) => {
          const dataUrl = liveThumbnails[idx] || thumbMap[idx];
          const isLoading = !dataUrl && loadingThumbsRef.current.has(idx);
          const isActive = idx === activePageIndex;

          return (
            <button
              key={idx}
              // BUG-13: attach sentinel ref to first rendered item to measure real height
              ref={idx === visibleIndices[0] ? sentinelItemRef : undefined}
              onClick={() => handleClick(idx)}
              className={`w-full flex items-center gap-2 p-1 rounded text-left group focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-accent ${
                isActive
                  ? 'rail-pages-thumb-active'
                  : 'hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
              title={`Go to page ${idx + 1}${dataUrl ? '' : ' (loading preview)'}`}
              aria-label={`Page ${idx + 1} thumbnail`}
              aria-current={isActive ? 'page' : undefined}
            >
              <div
                className="flex-shrink-0 border border-slate-200 dark:border-slate-600 rounded overflow-hidden bg-white dark:bg-slate-800"
                style={{ width: 52, minHeight: 58 }}
              >
                {dataUrl ? (
                  <img
                    src={dataUrl}
                    alt={`Page ${idx + 1}`}
                    className="block w-[52px] h-auto"
                    loading="lazy"
                  />
                ) : (
                  <div
                    className={`w-[52px] ${isLoading ? 'animate-pulse bg-slate-200 dark:bg-slate-700' : 'bg-slate-100 dark:bg-slate-800'}`}
                    style={{ height: 58 }}
                    aria-hidden="true"
                  />
                )}
              </div>
              <span className="text-xs text-slate-600 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white tabular-nums">
                Page {idx + 1}
              </span>
            </button>
          );
        })}

        {/* Bottom spacer */}
        <div style={{ height: `${bottomSpacerH}px` }} aria-hidden="true" />

        {visibleIndices.length === 0 && (
          <div className="text-xs text-slate-400 px-1 py-1">No pages</div>
        )}
      </div>
      {/* Subtle hint for very large docs (only when many pages) */}
      {pageCount > 30 && (
        <div className="text-[9px] text-slate-400/70 px-1 pt-0.5">Virtualized ({visibleIndices.length} of {pageCount})</div>
      )}
    </div>
  );
};
